import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  IconButton,
  Modal,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  ABO_COLORS,
} from "../lib/campaignAboStyles";
import {
  buildCampaignAboLogsUrl,
  buildChildLogsFiltering,
  buildParentLogsFiltering,
  CAMPAIGN_ABO_CHILD_RULE_TYPE,
  CAMPAIGN_ABO_LOG_COLUMNS,
  CAMPAIGN_ABO_LOGS_CHILD_SKELETON_ROWS,
  CAMPAIGN_ABO_LOGS_PARENT_SKELETON_ROWS,
  CAMPAIGN_ABO_PARENT_RULE_TYPE,
  mapAboLogToRow,
  parseLogsApiList,
} from "../lib/campaignAboLogs";

const EXPANDED_ROW_BG = "rgba(97, 72, 246, 0.08)";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "96%",
  maxWidth: 1200,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerCellSx = {
  fontWeight: 700,
  fontSize: 11,
  color: "text.secondary",
  bgcolor: "grey.100",
  py: 1.25,
  whiteSpace: "nowrap",
};

function getParentRowKey(row) {
  return `${row.trackId}::${row.timestamp}::${row.event}`;
}

function LogCell({ value }) {
  const text = value == null || value === "" ? "—" : value;
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontSize: "0.8125rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
    >
      {text}
    </Typography>
  );
}

function CenteredMessage({ children, minHeight = 120 }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1}
      minHeight={minHeight}
      py={3}
    >
      {children}
    </Box>
  );
}

function LogSkeletonRows({ count, isChild = false }) {
  return Array.from({ length: count }, (_, rowIndex) => (
    <TableRow
      key={`log-skeleton-${isChild ? "child" : "parent"}-${rowIndex}`}
      sx={isChild ? { bgcolor: EXPANDED_ROW_BG } : undefined}
    >
      <TableCell sx={{ width: 48, py: 1 }}>
        {!isChild ? <Skeleton variant="circular" width={24} height={24} /> : null}
      </TableCell>
      {CAMPAIGN_ABO_LOG_COLUMNS.map((col, colIndex) => (
        <TableCell key={col.key} sx={{ minWidth: col.minWidth, py: 1 }}>
          <Skeleton
            variant="text"
            width={colIndex % 3 === 0 ? "92%" : colIndex % 3 === 1 ? "72%" : "58%"}
            height={22}
          />
        </TableCell>
      ))}
    </TableRow>
  ));
}

function LogDataRow({ row, isChild = false }) {
  return (
    <TableRow sx={isChild ? { bgcolor: EXPANDED_ROW_BG } : undefined} hover={!isChild}>
      <TableCell sx={{ width: 48 }} />
      {CAMPAIGN_ABO_LOG_COLUMNS.map((col) => (
        <TableCell key={col.key} sx={{ minWidth: col.minWidth, verticalAlign: "top" }}>
          <LogCell value={row[col.key]} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function ExpandedChildSection({ parentRow, childRows, childLoading, childError }) {
  const colSpan = CAMPAIGN_ABO_LOG_COLUMNS.length + 1;

  if (childLoading) {
    return (
      <LogSkeletonRows count={CAMPAIGN_ABO_LOGS_CHILD_SKELETON_ROWS} isChild />
    );
  }

  if (childError) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <Typography variant="body2" color="error" py={1.5} px={2}>
            {childError}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  if (!childRows.length) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <CenteredMessage minHeight={80}>
            <Typography variant="body2" color="text.secondary">
              No campaign-level logs found
            </Typography>
          </CenteredMessage>
        </TableCell>
      </TableRow>
    );
  }

  return childRows.map((childRow) => (
    <LogDataRow key={childRow.rowKey || `${parentRow.rowKey}-child`} row={childRow} isChild />
  ));
}

export default function CampaignAboLogsModal({ open, onClose, ruleName, clientId, source }) {
  const [expanded, setExpanded] = useState(new Set());
  const [parentRows, setParentRows] = useState([]);
  const [parentLoading, setParentLoading] = useState(false);
  const [parentError, setParentError] = useState("");
  const [childRowsMap, setChildRowsMap] = useState({});
  const [childLoadingMap, setChildLoadingMap] = useState({});
  const [childErrorMap, setChildErrorMap] = useState({});

  const resetState = useCallback(() => {
    setExpanded(new Set());
    setParentRows([]);
    setParentLoading(false);
    setParentError("");
    setChildRowsMap({});
    setChildLoadingMap({});
    setChildErrorMap({});
  }, []);

  const fetchParentLogs = useCallback(async () => {
    if (!clientId || !source) return;

    setParentLoading(true);
    setParentError("");

    try {
      const url = buildCampaignAboLogsUrl(clientId, {
        source,
        filtering: buildParentLogsFiltering(ruleName),
        ruleType: CAMPAIGN_ABO_PARENT_RULE_TYPE,
      });
      const { data } = await axios.get(url);
      setParentRows(parseLogsApiList(data).map(mapAboLogToRow));
    } catch (error) {
      setParentError(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Failed to fetch logs"
      );
      setParentRows([]);
    } finally {
      setParentLoading(false);
    }
  }, [clientId, source, ruleName]);

  const fetchChildLogs = useCallback(
    async (parentRow) => {
      const parentKey = getParentRowKey(parentRow);
      const trackId = parentRow.trackId;

      if (!clientId || !source || !trackId) {
        setChildErrorMap((prev) => ({
          ...prev,
          [parentKey]: "Missing track id for child logs",
        }));
        return;
      }

      setChildLoadingMap((prev) => ({ ...prev, [parentKey]: true }));
      setChildErrorMap((prev) => ({ ...prev, [parentKey]: "" }));

      try {
        const url = buildCampaignAboLogsUrl(clientId, {
          source,
          filtering: buildChildLogsFiltering(trackId),
          ruleType: CAMPAIGN_ABO_CHILD_RULE_TYPE,
        });
        const { data } = await axios.get(url);
        setChildRowsMap((prev) => ({
          ...prev,
          [parentKey]: parseLogsApiList(data).map((log) => mapAboLogToRow(log, { isChild: true })),
        }));
      } catch (error) {
        setChildErrorMap((prev) => ({
          ...prev,
          [parentKey]:
            error?.response?.data?.message ??
              error?.response?.data?.error ??
              error?.message ??
              "Failed to fetch campaign logs",
        }));
      } finally {
        setChildLoadingMap((prev) => ({ ...prev, [parentKey]: false }));
      }
    },
    [clientId, source]
  );

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  useEffect(() => {
    if (!open || !clientId || !source) return;
    fetchParentLogs();
  }, [open, clientId, source, fetchParentLogs]);

  const handleToggleRow = useCallback(
    (row) => {
      const parentKey = getParentRowKey(row);
      setExpanded((prev) => {
        const next = new Set(prev);
        const isOpening = !next.has(parentKey);

        if (next.has(parentKey)) {
          next.delete(parentKey);
        } else {
          next.add(parentKey);
        }

        if (isOpening) {
          fetchChildLogs(row);
        }

        return next;
      });
    },
    [fetchChildLogs]
  );

  const colSpan = CAMPAIGN_ABO_LOG_COLUMNS.length + 1;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          px={2.5}
          py={2}
          borderBottom={1}
          borderColor="divider"
          flexShrink={0}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: ABO_COLORS.title }}>
              Campaign ABO Logs
            </Typography>
            {ruleName ? (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Rule Name: <strong>{ruleName}</strong>
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Expand a row to view campaign-level log details.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close logs" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box flex={1} overflow="hidden" p={1.25}>
          <TableContainer
            sx={{
              maxHeight: "calc(90vh - 140px)",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 48 }} />
                  {CAMPAIGN_ABO_LOG_COLUMNS.map((col) => (
                    <TableCell key={col.key} sx={{ ...headerCellSx, minWidth: col.minWidth }}>
                      {col.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parentLoading ? (
                  <LogSkeletonRows count={CAMPAIGN_ABO_LOGS_PARENT_SKELETON_ROWS} />
                ) : parentError ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <CenteredMessage minHeight={120}>
                        <Typography variant="body2" color="error">
                          {parentError}
                        </Typography>
                      </CenteredMessage>
                    </TableCell>
                  </TableRow>
                ) : !parentRows.length ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <CenteredMessage minHeight={120}>
                        <Typography variant="body2" color="text.secondary">
                          No logs found
                        </Typography>
                      </CenteredMessage>
                    </TableCell>
                  </TableRow>
                ) : (
                  parentRows.map((row) => {
                    const parentKey = getParentRowKey(row);
                    const isOpen = expanded.has(parentKey);

                    return (
                      <Fragment key={parentKey}>
                        <TableRow
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleToggleRow(row)}
                        >
                          <TableCell sx={{ width: 48, py: 0.5 }}>
                            <IconButton
                              size="small"
                              aria-label={isOpen ? "Collapse row" : "Expand row"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleRow(row);
                              }}
                            >
                              {isOpen ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          {CAMPAIGN_ABO_LOG_COLUMNS.map((col) => (
                            <TableCell
                              key={col.key}
                              sx={{ minWidth: col.minWidth, verticalAlign: "top" }}
                            >
                              <LogCell value={row[col.key]} />
                            </TableCell>
                          ))}
                        </TableRow>
                        {isOpen ? (
                          <ExpandedChildSection
                            parentRow={row}
                            childRows={childRowsMap[parentKey] || []}
                            childLoading={childLoadingMap[parentKey]}
                            childError={childErrorMap[parentKey]}
                          />
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Modal>
  );
}
